
import React, { useState } from 'react';
import { Release, Status, Environment, SubItem } from '@/types';
import { Button } from '@/components/ui/button';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { DetailsItem, DetailsLink, DetailsText, DetailsIcons } from '@/components/ui/details';
import { useNavigate } from 'react-router-dom';
import SubItemList from './SubItemList';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface ReleaseCardProps {
  release: Release;
  updateRelease: (release: Release) => void;
  deleteRelease: (id: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  clientId?: string; // Added clientId prop
  defaultOpen?: boolean; // Add defaultOpen prop to control initial expansion state
}

// Status colors for badges (white bg with colored text)
const statusColors = {
  'released': 'bg-white text-[#10b981] border border-[#10b981]', // release-released
  'ready for release': 'bg-white text-[#3b82f6] border border-[#3b82f6]', // release-ready
  'not ready': 'bg-white text-[#f59e0b] border border-[#f59e0b]', // release-not-ready
  'on hold': 'bg-white text-[#ef4444] border border-[#ef4444]' // release-on-hold
};

// Header background colors for status sections
const statusHeaderColors = {
  'released': 'bg-[#10b981] text-white', // green for released
  'ready for release': 'bg-[#3b82f6] text-white', // blue for ready
  'not ready': 'bg-[#f59e0b] text-white', // yellow/orange for not ready
  'on hold': 'bg-[#ef4444] text-white' // red for on hold
};

// Border colors for status-based card styling
const statusBorderColors = {
  'released': 'border-[#10b981]', // green for released
  'ready for release': 'border-[#3b82f6]', // blue for ready
  'not ready': 'border-[#f59e0b]', // yellow/orange for not ready
  'on hold': 'border-[#ef4444]' // red for on hold
};

const environmentColors = {
  'staging': 'bg-purple-100 text-purple-800',
  'live': 'bg-red-100 text-red-800'
};

const ReleaseCard: React.FC<ReleaseCardProps> = ({
  release,
  updateRelease, 
  deleteRelease, 
  canEdit = true,
  clientId,
  defaultOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const navigate = useNavigate();

  const handleUpdateSubItem = (updatedSubItem: any) => {
    updateRelease({
      ...release,
      subItems: release.subItems.map(item => 
        item.id === updatedSubItem.id ? updatedSubItem : item
      )
    });
  };

  const handleDeleteSubItem = (id: string) => {
    updateRelease({
      ...release,
      subItems: release.subItems.filter(item => item.id !== id)
    });
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Unscheduled';
    
    // Format the date directly using the original date object
    // This ensures we display exactly what's in the database without timezone adjustments
    const options: Intl.DateTimeFormatOptions = { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric', 
      timeZone: 'UTC' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Use planned release date if available, otherwise fall back to created date
  const displayDate = release.plannedReleaseDate ? formatDate(release.plannedReleaseDate) : formatDate(release.createdAt);

  return (
    <Card className={`mb-4 bg-white border-2 rounded-lg overflow-hidden ${statusBorderColors[release.status as Status]}`}>
      <div className={`p-4 ${statusHeaderColors[release.status as Status]}`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-medium">
              v{release.version} - {displayDate}
            </h2>
            <Badge className={statusColors[release.status as Status]}>{release.status}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {/* View Release Button - updated to show text */}
            <Button 
              variant="outline" 
              size="sm"
              className="bg-white text-black hover:bg-gray-100"
              onClick={() => navigate(`/clients/${clientId}/releases/${release.id}`)}
            >
              <ArrowRight size={16} className="mr-1" />
              View Details
            </Button>
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>
      </div>

      <Collapsible open={isOpen}>
        <CollapsibleContent>
          <div className="p-4 border-b space-y-6">
            {/* Two-column layout for details */}
            <div className="grid grid-cols-2 gap-6">
              {/* Left column */}
              <div>
                <DetailsItem icon={<DetailsIcons.Globe size={16} />} label="Environment">
                  <Badge className={environmentColors[release.environment as Environment]}>
                    {release.environment}
                  </Badge>
                </DetailsItem>
                
                <DetailsText 
                  text={release.approvedBy} 
                  label="Approved By" 
                  icon={<DetailsIcons.User size={16} />}
                  className="mt-4"
                />
              </div>
              
              {/* Right column */}
              <div>
                <DetailsLink 
                  href={release.brdLink} 
                  label="BRD Link" 
                />
                
                {release.plannedReleaseDate && (
                  <div className="mt-4">
                    <DetailsItem icon={<DetailsIcons.FileText size={16} />} label="Planned Release Date">
                      <p className="text-sm">{formatDate(release.plannedReleaseDate)}</p>
                    </DetailsItem>
                  </div>
                )}
              </div>
            </div>
            
            {/* Full-width notes */}
            {release.notes && (
              <div className="mt-6">
                <div className="bg-white p-4 rounded-md border border-gray-100 shadow-sm">
                  <MarkdownContent content={release.notes} />
                </div>
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="mb-4">
            </div>
            
            <SubItemList 
              subItems={release.subItems} 
              updateSubItem={handleUpdateSubItem}
              deleteSubItem={handleDeleteSubItem}
              canEdit={canEdit}
              releaseId={release.id}
              clientId={clientId}
              showActions={false} // Don't show actions in the release card
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default ReleaseCard;
